/**
 * Anthropic Messages API adapter for the Excel multi-hop executor.
 *
 * Where the OpenAI Responses API accepts a flat list of items in any order,
 * Messages requires a strictly alternating `user` / `assistant` transcript of
 * whole messages, each holding an ordered array of content blocks. The three
 * structural consequences handled here are:
 *
 *  1. Runs of `tool_result` and `user` turns collapse into ONE user message
 *     (`encodeTurns`), with the tool results ahead of the orchestrator text so
 *     they directly follow the assistant turn that requested them.
 *  2. Compaction operates on whole messages rather than single items, so the
 *     recent-window budget is re-derived in message pairs (`projectItems`).
 *  3. Assistant turns are replayed verbatim, thinking blocks and their
 *     signatures intact. Anthropic rejects a tool-use turn whose assistant
 *     message lost its thinking blocks, and the signature is what lets the
 *     server reconstruct the original reasoning.
 */

import { buildApiUrl } from "../../shared/config";
import { isAbortError } from "../../shared/utils/abortError";
import {
  isTimeoutFetchError,
  isTransientFetchError,
  readModelError,
} from "../../shared/utils/http";
import { buildToolDefinitionsForProvider } from "../toolRegistry";
import {
  MAX_REPLAYED_TOOL_OUTPUT_CHARS,
  truncateReplayedToolOutput,
} from "./replayTruncation";
import type { ExecutorReasoningEffort } from "./registry";
import type {
  AgentToolCall,
  AgentTurn,
  AgentTurnResult,
  ExecutorBuildRequestParams,
  ExecutorProviderAdapter,
  ExecutorProviderRequest,
  ExecutorSerializeToolsParams,
  ExecutorStreamTurnParams,
} from "./types";

// ---------------------------------------------------------------------------
// Messages API types (minimal)
// ---------------------------------------------------------------------------

type AnthropicContentBlock = Record<string, unknown>;

interface AnthropicMessage {
  role: "user" | "assistant";
  content: AnthropicContentBlock[];
}

/** Stand-in output for a `tool_use` the runner never reported a result for. */
const UNRESOLVED_TOOL_RESULT_OUTPUT =
  "This tool call produced no result (the run moved on before it completed).";

/** Keeps a merged user message from being emitted with zero content blocks. */
const EMPTY_USER_MESSAGE_PLACEHOLDER = "Continue.";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function blockType(block: unknown): string {
  return isRecord(block) && typeof block.type === "string" ? block.type : "";
}

// ---------------------------------------------------------------------------
// Turn encoding
// ---------------------------------------------------------------------------

/**
 * One in-progress message. User groups keep tool results separate from the rest
 * so the flattened content always leads with `tool_result` blocks, whatever
 * order the runner appended its turns in.
 */
interface MessageGroup {
  role: "user" | "assistant";
  toolResults: AnthropicContentBlock[];
  rest: AnthropicContentBlock[];
}

function flattenGroup(group: MessageGroup): AnthropicMessage {
  const content = [...group.toolResults, ...group.rest];
  if (content.length === 0 && group.role === "user") {
    content.push({ type: "text", text: EMPTY_USER_MESSAGE_PLACEHOLDER });
  }
  return { role: group.role, content };
}

/**
 * Rewrites an orphaned `tool_result` (no matching `tool_use` in the preceding
 * assistant message) as plain text. Anthropic rejects the unpaired block, but
 * the output itself is still context the model should see.
 */
function orphanedToolResultAsText(block: AnthropicContentBlock): AnthropicContentBlock {
  const id = typeof block.tool_use_id === "string" ? block.tool_use_id : "unknown";
  const content = block.content;
  const rendered =
    typeof content === "string" ? content : JSON.stringify(content ?? null);
  return { type: "text", text: `Tool result (${id}): ${rendered}` };
}

/**
 * Enforces the two pairing rules the Messages API validates: every `tool_use`
 * in an assistant message must be answered by a `tool_result` at the head of
 * the next user message, and no `tool_result` may reference a `tool_use` that
 * is not in the message immediately before it.
 *
 * The runner appends a result for every call it dispatches, so this is a
 * backstop: an unpaired block would fail the whole request with a 400 rather
 * than degrade, which is far too expensive mid-run.
 */
function repairToolPairing(messages: AnthropicMessage[]): AnthropicMessage[] {
  const repaired: AnthropicMessage[] = [];
  for (let i = 0; i < messages.length; i += 1) {
    const message = messages[i];
    if (message.role === "user") {
      const previous = repaired[repaired.length - 1];
      const openIds = new Set(
        previous?.role === "assistant"
          ? previous.content
              .filter((block) => blockType(block) === "tool_use")
              .map((block) => String(block.id ?? ""))
          : [],
      );
      const content = message.content.map((block) =>
        blockType(block) === "tool_result" &&
        !openIds.has(String(block.tool_use_id ?? ""))
          ? orphanedToolResultAsText(block)
          : block,
      );
      repaired.push({ role: "user", content });
      continue;
    }
    repaired.push(message);

    const pendingIds = message.content
      .filter((block) => blockType(block) === "tool_use")
      .map((block) => String(block.id ?? ""))
      .filter(Boolean);
    if (pendingIds.length === 0) continue;

    const next = messages[i + 1];
    const answeredIds = new Set(
      next?.role === "user"
        ? next.content
            .filter((block) => blockType(block) === "tool_result")
            .map((block) => String(block.tool_use_id ?? ""))
        : [],
    );
    const stubs = pendingIds
      .filter((id) => !answeredIds.has(id))
      .map((id) => ({
        type: "tool_result",
        tool_use_id: id,
        content: UNRESOLVED_TOOL_RESULT_OUTPUT,
        is_error: true,
      }));
    if (stubs.length === 0) continue;
    if (next?.role === "user") {
      // Consumed on the next iteration, which re-reads `messages[i + 1]`.
      next.content = [...stubs, ...next.content];
    } else {
      messages.splice(i + 1, 0, { role: "user", content: stubs });
    }
  }
  return repaired;
}

/**
 * Flattens the canonical IR into an alternating Messages transcript.
 *
 * Model turns are spread back in verbatim so signed thinking blocks survive the
 * round-trip. Every other turn contributes to the current user group, which is
 * only closed when the next assistant turn arrives.
 */
function encodeTurns(turns: readonly AgentTurn[]): unknown[] {
  const groups: MessageGroup[] = [];
  const groupFor = (role: "user" | "assistant"): MessageGroup => {
    const last = groups[groups.length - 1];
    if (last && last.role === role) return last;
    const group: MessageGroup = { role, toolResults: [], rest: [] };
    groups.push(group);
    return group;
  };

  for (const turn of turns) {
    if (turn.kind === "model") {
      const group = groupFor("assistant");
      for (const block of turn.raw) {
        if (isRecord(block)) group.rest.push(block);
      }
      continue;
    }
    if (turn.kind === "tool_result") {
      groupFor("user").toolResults.push({
        type: "tool_result",
        tool_use_id: turn.callId,
        content: turn.output,
      });
      continue;
    }
    groupFor("user").rest.push({ type: "text", text: turn.content });
  }

  // An assistant turn is only empty if the provider returned nothing. Dropping
  // it can leave two user messages adjacent, so re-merge by role afterwards to
  // keep the transcript strictly alternating.
  const messages: AnthropicMessage[] = [];
  for (const message of groups.map(flattenGroup)) {
    if (message.content.length === 0) continue;
    const last = messages[messages.length - 1];
    if (last && last.role === message.role) {
      last.content.push(...message.content);
      continue;
    }
    messages.push(message);
  }
  return repairToolPairing(messages);
}

// ---------------------------------------------------------------------------
// History compaction
// ---------------------------------------------------------------------------

/**
 * Recent-window budget, re-derived for whole messages.
 *
 * The OpenAI adapter keeps the last 12 Responses ITEMS, roughly four hops —
 * fewer once the model calls tools in parallel, since each extra call adds two
 * more items.
 *
 * One encoded Anthropic item is a whole message, and a hop is always exactly
 * two of them (assistant + merged user), however many tools the turn called.
 * Eight messages therefore reproduces the same four-hop verbatim window and,
 * unlike the item count, does not shrink under parallel tool use. Everything
 * older is still replayed in full; only its oversized tool outputs shrink.
 */
const RECENT_MESSAGES_KEPT_FULL = 8;

/**
 * Compacts one older message by shrinking large tool outputs.
 *
 * Note what is deliberately NOT dropped: thinking blocks. The OpenAI adapter
 * discards superseded `reasoning` items, but the equivalent saving is not
 * available here and not worth chasing. Anthropic validates that a replayed
 * assistant message arrives unmodified and rejects a rebuilt one, it filters
 * older thinking server-side anyway, and it bills only for the blocks it
 * actually puts in context. Removing them client-side risks a hard 400 in
 * exchange for tokens the API was already not charging for.
 *
 * Returns the original object when nothing changed, so the projection stays
 * cheap and mostly reference-identical.
 */
function compactOlderMessage(item: unknown): unknown {
  if (!isRecord(item) || !Array.isArray(item.content)) return item;
  let changed = false;
  const content = item.content.map((block) => {
    if (blockType(block) !== "tool_result" || !isRecord(block)) return block;
    const output = block.content;
    if (typeof output !== "string" || output.length <= MAX_REPLAYED_TOOL_OUTPUT_CHARS) {
      return block;
    }
    changed = true;
    return { ...block, content: truncateReplayedToolOutput(output) };
  });
  return changed ? { ...item, content } : item;
}

/**
 * Builds the compacted transcript sent on the next request while leaving the
 * canonical conversation untouched. The seed user message (request + plan +
 * workbook context) and the recent window are always replayed verbatim.
 */
function projectItems(items: readonly unknown[]): unknown[] {
  const cutoff = Math.max(0, items.length - RECENT_MESSAGES_KEPT_FULL);
  return items.map((item, index) =>
    index === 0 || index >= cutoff ? item : compactOlderMessage(item),
  );
}

// ---------------------------------------------------------------------------
// Request building
// ---------------------------------------------------------------------------

/** `max_tokens` is mandatory on Messages, unlike OpenAI's optional cap. */
const DEFAULT_MAX_OUTPUT_TOKENS = 8192;

type AnthropicEffort = "low" | "medium" | "high";

/**
 * Maps the registry's reasoning effort onto Anthropic's `output_config.effort`.
 *
 * Effort is Anthropic's counterpart to `reasoning.effort`, but it is broader:
 * it scales every token in the response, tool calls included, not just
 * thinking. There is no `minimal` level, so it floors at `low`. The higher
 * `xhigh` and `max` levels exist on current Opus models but are unreachable
 * from the shared registry enum, which is deliberate for a 200-iteration ReAct
 * loop.
 */
const EFFORT_BY_REASONING_EFFORT: Record<ExecutorReasoningEffort, AnthropicEffort> = {
  minimal: "low",
  low: "low",
  medium: "medium",
  high: "high",
};

/**
 * Thinking configuration.
 *
 * The Opus models this executor runs on use ADAPTIVE thinking, which is on by
 * default and lets the model decide when and how deeply to think. Manual
 * extended thinking (`{type:"enabled", budget_tokens}`) is not just discouraged
 * here - it is rejected with a 400 on Claude 4.7 and later, so there is no
 * budget to derive.
 *
 * The one thing worth setting explicitly is `display`: it defaults to
 * `"omitted"`, which suppresses `thinking_delta` frames entirely and would
 * leave the executor's live reasoning trace blank. `"summarized"` keeps parity
 * with the OpenAI path's `reasoning.summary: "auto"`.
 */
const ADAPTIVE_THINKING = { type: "adaptive", display: "summarized" } as const;

// ---------------------------------------------------------------------------
// Prompt caching
//
// Three breakpoints are placed, in the `tools` → `system` → `messages` order
// Anthropic reads a prompt as one prefix:
//
//  1. the last tool definition, which caches the whole `tools` array,
//  2. the system prompt, ~45K characters re-sent on every hop,
//  3. a rolling breakpoint on the last block of the transcript.
//
// (1) and (2) are the ones that pay. Together they are the static prefix that
// every one of up to 200 iterations re-sends byte-identical, and because they
// sit ahead of `messages` in the hierarchy they keep hitting even on a turn
// where the transcript's own entry misses.
//
// (3) stops the transcript being re-billed as it grows. It has to be the LAST
// block rather than a fixed index: a fixed index would stop moving forward and
// cache an ever-smaller share of the conversation. Placing it on the final
// block is safe here specifically because the transcript is append-only - the
// runner only ever pushes turns - so the prefix behind the breakpoint is
// unchanged next turn, and the entry this turn writes is found by the lookback
// Anthropic runs back from the following turn's breakpoint (20 positions, and
// one hop adds about four blocks). The usual way a rolling breakpoint fails,
// keying its entry to a block that varies per request so nothing ever reads it
// back, needs a mutable suffix and there is none here.
//
// What does still miss is a turn where `projectItems` truncates a tool output
// that has just left the recent window: that rewrites a block behind the
// breakpoint, so the transcript entry is invalidated and written again once.
// The tools and system entries survive it, which is why those two are worth
// their own breakpoints rather than being folded into the rolling one.
//
// The fourth slot stays free deliberately. It is the headroom for a second
// rolling breakpoint (a hop wide enough to add 20 blocks at once would push the
// previous write out of a single lookback window) or a one-hour TTL on the
// static prefix - and unlike a cache miss, exceeding the limit fails the run.
// ---------------------------------------------------------------------------

/**
 * Anthropic rejects a request carrying more than this many `cache_control`
 * breakpoints with a 400, which would fail the whole run rather than degrade
 * one turn. Each helper below marks at most one block, so the count is fixed at
 * three by construction and does not grow with the conversation.
 */
export const MAX_CACHE_BREAKPOINTS = 4;

const EPHEMERAL_CACHE_CONTROL = { type: "ephemeral" } as const;

/**
 * Whether Anthropic accepts a `cache_control` on this block.
 *
 * Thinking blocks are the exclusion that matters: they cannot be marked
 * directly, though they are still cached as part of the assistant turn around
 * them. Empty text blocks are not cacheable either.
 */
function acceptsCacheControl(block: unknown): boolean {
  if (!isRecord(block)) return false;
  const type = blockType(block);
  if (type === "thinking" || type === "redacted_thinking") return false;
  if (type === "text") return typeof block.text === "string" && block.text.trim() !== "";
  return type !== "";
}

function markedWithCacheControl(block: Record<string, unknown>): AnthropicContentBlock {
  return { ...block, cache_control: EPHEMERAL_CACHE_CONTROL };
}

/**
 * The system prompt as a cached text block.
 *
 * `system` has to become a block array for a breakpoint to have anywhere to
 * sit; the plain string form carries no place for one. An empty prompt keeps
 * the string form, since an empty text block is not cacheable and there would
 * be nothing to cache regardless.
 */
function buildCachedSystem(instructions: string): unknown {
  if (!instructions.trim()) return instructions;
  return [markedWithCacheControl({ type: "text", text: instructions })];
}

/**
 * The tool definitions with a breakpoint on the last one, which caches every
 * tool before and including it as a single prefix.
 *
 * Copied rather than marked in place: the runner serializes the tools once and
 * hands the same array back on every iteration, so marking that array would
 * leave the run's own tool definitions altered.
 */
function withCachedToolDefinitions(tools: readonly unknown[]): unknown[] {
  const marked = tools.slice();
  const lastIndex = marked.length - 1;
  const last = marked[lastIndex];
  if (!isRecord(last)) return marked;
  marked[lastIndex] = markedWithCacheControl(last);
  return marked;
}

/** Index of the last block in a message that can carry a breakpoint, or -1. */
function lastCacheableBlockIndex(content: readonly unknown[]): number {
  for (let index = content.length - 1; index >= 0; index -= 1) {
    if (acceptsCacheControl(content[index])) return index;
  }
  return -1;
}

/**
 * The transcript with the rolling breakpoint on its last cacheable block.
 *
 * Copies down to the single block it marks. `projectItems` returns an older
 * message by reference whenever it had nothing to compact, so marking in place
 * would write `cache_control` into the canonical conversation and replay it on
 * every later turn.
 *
 * Scanning backwards is what keeps a turn ending in an unmarkable block (a bare
 * thinking block, an empty text block) caching a slightly shorter prefix rather
 * than caching nothing at all.
 */
function withRollingCacheBreakpoint(items: readonly unknown[]): unknown[] {
  const marked = items.slice();
  for (let index = marked.length - 1; index >= 0; index -= 1) {
    const message = marked[index];
    if (!isRecord(message) || !Array.isArray(message.content)) continue;
    const blockIndex = lastCacheableBlockIndex(message.content);
    if (blockIndex < 0) continue;
    const content = message.content.slice();
    content[blockIndex] = markedWithCacheControl(
      content[blockIndex] as Record<string, unknown>,
    );
    marked[index] = { ...message, content };
    return marked;
  }
  return marked;
}

function buildRequest({
  model,
  instructions,
  items,
  tools,
  forcedToolName,
}: ExecutorBuildRequestParams): ExecutorProviderRequest {
  return {
    url: buildApiUrl("/api/ai/anthropic/messages"),
    body: {
      model: model.model,
      system: buildCachedSystem(instructions),
      messages: withRollingCacheBreakpoint(items),
      tools: withCachedToolDefinitions(tools),
      // Adaptive thinking supports forced tool use, so the runner's
      // `forcedToolChoiceName` maps across intact. (Manual extended thinking
      // would have rejected it, which is a second reason not to use that mode.)
      tool_choice: forcedToolName
        ? { type: "tool", name: forcedToolName }
        : { type: "auto" },
      max_tokens: model.maxOutputTokens ?? DEFAULT_MAX_OUTPUT_TOKENS,
      thinking: ADAPTIVE_THINKING,
      output_config: { effort: EFFORT_BY_REASONING_EFFORT[model.reasoningEffort] ?? "low" },
      stream: true,
    },
  };
}

// ---------------------------------------------------------------------------
// Streaming
// ---------------------------------------------------------------------------

export class AnthropicStreamError extends Error {
  code: string;
  transient: boolean;
  details?: unknown;

  constructor(
    message: string,
    options: { code: string; transient?: boolean; details?: unknown },
  ) {
    super(message);
    this.name = "AnthropicStreamError";
    this.code = options.code;
    this.transient = options.transient === true;
    this.details = options.details;
  }
}

/**
 * Anthropic error envelopes that are worth one replay. `invalid_request_error`
 * and the auth/permission family are deterministic and must surface instead.
 */
const TRANSIENT_ANTHROPIC_ERROR_TYPES = new Set([
  "api_error",
  "overloaded_error",
  "rate_limit_error",
  "timeout_error",
]);

/**
 * The proxy performs no retry of its own, so upstream 429/5xx reach the client
 * with their original status and are replayed here instead.
 */
const TRANSIENT_HTTP_STATUSES = new Set([408, 409, 429, 500, 502, 503, 504, 529]);

/** Upper bound on honouring a `retry-after` before a stream replay. */
const MAX_RETRY_AFTER_MS = 5000;

function isTransientAnthropicStreamError(error: unknown): boolean {
  return (
    (error instanceof AnthropicStreamError && error.transient) ||
    isTimeoutFetchError(error) ||
    isTransientFetchError(error)
  );
}

function parseRetryAfterMs(response: Response): number {
  const header = response.headers?.get?.("retry-after");
  if (!header) return 0;
  const seconds = Number.parseFloat(header);
  if (!Number.isFinite(seconds) || seconds <= 0) return 0;
  return Math.min(seconds * 1000, MAX_RETRY_AFTER_MS);
}

/** One content block being assembled from its `content_block_*` frames. */
interface StreamingBlock {
  start: Record<string, unknown>;
  type: string;
  text: string;
  thinking: string;
  signature: string;
  partialJson: string;
}

function toRawBlock(block: StreamingBlock): AnthropicContentBlock {
  const raw: AnthropicContentBlock = { ...block.start, type: block.type };
  if (block.type === "tool_use") {
    raw.input = safeParseToolInput(block.partialJson, block.start.input);
    return raw;
  }
  if (block.type === "thinking") {
    raw.thinking = block.thinking;
    // Unsigned thinking is rejected on replay, so only attach a real signature.
    if (block.signature) raw.signature = block.signature;
    return raw;
  }
  if (block.type === "redacted_thinking") return raw;
  if (block.type === "text") {
    raw.text = block.text;
    return raw;
  }
  // Unknown block types (e.g. future server-tool blocks) round-trip as sent.
  return raw;
}

function safeParseToolInput(partialJson: string, fallback: unknown): unknown {
  if (!partialJson.trim()) return isRecord(fallback) ? fallback : {};
  try {
    return JSON.parse(partialJson);
  } catch {
    // Kept verbatim: `runCall` re-parses the argument string and reports the
    // malformed payload back to the model rather than failing the run.
    return isRecord(fallback) ? fallback : {};
  }
}

/**
 * Reads one chunk, reporting a mid-stream transport failure as a typed stream
 * error rather than whatever the platform threw.
 *
 * A stream that ends cleanly but early is already reported below as an
 * `AnthropicStreamError`, and only that type is recognized as a fatal provider
 * turn. A socket that drops mid-frame instead rejects `read()` with a raw
 * network error, which escapes `runMultiHopExecutor` rather than ending the run
 * on the bounded path that reports the workbook edits it already applied. Both
 * are the same failure and must end the run the same way.
 *
 * The scope is deliberately the read alone. A caller abort is rethrown
 * untouched so it keeps stopping the run immediately, and a fault in the frame
 * handling below stays raw so a programming error is never disguised as a
 * provider failure.
 */
async function readStreamChunk(
  reader: ReadableStreamDefaultReader<Uint8Array>,
): Promise<ReadableStreamReadResult<Uint8Array>> {
  try {
    return await reader.read();
  } catch (error) {
    if (isAbortError(error) || error instanceof AnthropicStreamError) throw error;
    throw new AnthropicStreamError(
      `Anthropic streaming response failed mid-stream: ${
        error instanceof Error ? error.message : String(error)
      }`,
      {
        // Matches what `isTransientAnthropicStreamError` would have concluded
        // from the raw error, so the one replay this failure gets is unchanged.
        code: "stream_read_failed",
        transient: isTransientFetchError(error),
        details: error,
      },
    );
  }
}

/**
 * Consumes one Messages API SSE stream.
 *
 * The turn is spread across frames rather than delivered in a single terminal
 * envelope, so blocks are accumulated per `index`:
 *
 *  - `message_start`      — message shell and input usage
 *  - `content_block_start`— the block's type plus `tool_use` id/name
 *  - `content_block_delta`— `text_delta`, `thinking_delta`, `signature_delta`,
 *                           and `input_json_delta` fragments that must be
 *                           concatenated per block index
 *  - `content_block_stop` — block complete
 *  - `message_delta`      — `stop_reason` and output usage
 *  - `message_stop`       — end of turn; its absence means a truncated stream
 *
 * `signature_delta` is retained (unlike the server's observability tee, which
 * drops it) because an unsigned thinking block cannot be replayed.
 */
async function readAnthropicMessagesStream(
  response: Response,
  onText?: (partial: string) => void,
  onReasoning?: (partial: string) => void,
): Promise<AgentTurnResult> {
  const reader = response.body?.getReader();
  if (!reader) {
    // Typed for the same reason as a mid-stream drop: an `ok` streaming
    // response with no body is a transport failure, and the run must end on the
    // bounded path rather than throwing out of the executor.
    throw new AnthropicStreamError(
      "Anthropic streaming response did not include a readable body",
      { code: "stream_missing_body", transient: false },
    );
  }

  const decoder = new TextDecoder();
  let buffer = "";
  let textContent = "";
  let reasoningContent = "";
  let stopReason = "";
  let sawMessageStop = false;
  const blocks = new Map<number, StreamingBlock>();
  const blockIndices: number[] = [];

  const ensureBlock = (index: unknown, fallbackType: string): StreamingBlock => {
    const key = typeof index === "number" && Number.isInteger(index) ? index : -1;
    let block = blocks.get(key);
    if (!block) {
      blockIndices.push(key);
      block = {
        start: {},
        type: fallbackType || "text",
        text: "",
        thinking: "",
        signature: "",
        partialJson: "",
      };
      blocks.set(key, block);
    }
    return block;
  };

  const handleEvent = (eventText: string): void => {
    const dataLines = eventText
      .split(/\r?\n/)
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice("data:".length).replace(/^\s+/, ""));
    if (dataLines.length === 0) return;
    const dataText = dataLines.join("\n");
    if (!dataText || dataText === "[DONE]") return;

    let event: unknown;
    try {
      event = JSON.parse(dataText);
    } catch {
      return;
    }
    if (!isRecord(event)) return;

    switch (event.type) {
      case "content_block_start": {
        const start = isRecord(event.content_block) ? event.content_block : {};
        const block = ensureBlock(event.index, blockType(start));
        block.start = { ...start };
        if (typeof start.type === "string") block.type = start.type;
        if (typeof start.text === "string" && start.text) {
          block.text += start.text;
          textContent += start.text;
          onText?.(textContent);
        }
        if (typeof start.thinking === "string" && start.thinking) {
          block.thinking += start.thinking;
          reasoningContent += start.thinking;
          onReasoning?.(reasoningContent);
        }
        if (typeof start.signature === "string") block.signature = start.signature;
        return;
      }
      case "content_block_delta": {
        const delta = isRecord(event.delta) ? event.delta : {};
        if (delta.type === "text_delta" && typeof delta.text === "string") {
          const block = ensureBlock(event.index, "text");
          block.text += delta.text;
          textContent += delta.text;
          onText?.(textContent);
          return;
        }
        if (delta.type === "thinking_delta" && typeof delta.thinking === "string") {
          const block = ensureBlock(event.index, "thinking");
          block.thinking += delta.thinking;
          reasoningContent += delta.thinking;
          onReasoning?.(reasoningContent);
          return;
        }
        if (delta.type === "signature_delta" && typeof delta.signature === "string") {
          ensureBlock(event.index, "thinking").signature += delta.signature;
          return;
        }
        if (
          delta.type === "input_json_delta" &&
          typeof delta.partial_json === "string"
        ) {
          ensureBlock(event.index, "tool_use").partialJson += delta.partial_json;
        }
        return;
      }
      case "message_delta": {
        const delta = isRecord(event.delta) ? event.delta : {};
        if (typeof delta.stop_reason === "string") stopReason = delta.stop_reason;
        return;
      }
      case "message_stop": {
        sawMessageStop = true;
        return;
      }
      case "error": {
        const details = isRecord(event.error) ? event.error : event;
        const code =
          typeof details.type === "string" ? details.type : "anthropic_error";
        throw new AnthropicStreamError(
          typeof details.message === "string" && details.message.trim()
            ? details.message.trim()
            : "Anthropic streaming response failed",
          {
            code,
            transient: TRANSIENT_ANTHROPIC_ERROR_TYPES.has(code),
            details,
          },
        );
      }
      default:
        // `message_start`, `content_block_stop`, and `ping` need no bookkeeping;
        // the turn is reassembled from the accumulated blocks below.
        return;
    }
  };

  try {
    while (true) {
      const { value, done } = await readStreamChunk(reader);
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split(/\r?\n\r?\n/);
      buffer = events.pop() || "";
      for (const eventText of events) {
        handleEvent(eventText);
      }
    }
    buffer += decoder.decode();
    if (buffer.trim()) handleEvent(buffer);
  } catch (error) {
    try {
      await reader.cancel();
    } catch {
      // Best-effort cleanup before surfacing the stream error.
    }
    throw error;
  } finally {
    reader.releaseLock();
  }

  if (!sawMessageStop) {
    // The proxy emits no error frame when an upstream stream breaks after the
    // headers, so a missing `message_stop` is the only truncation signal.
    throw new AnthropicStreamError(
      "Anthropic streaming response ended before message_stop",
      { code: "stream_ended_before_completion", transient: true },
    );
  }
  if (stopReason === "max_tokens" || stopReason === "refusal") {
    throw new AnthropicStreamError(
      `Anthropic response ended with stop_reason ${stopReason}`,
      { code: `stop_${stopReason}`, transient: false },
    );
  }

  const ordered = blockIndices
    .slice()
    .sort((left, right) => left - right)
    .map((index) => blocks.get(index) as StreamingBlock);
  const raw = ordered.map(toRawBlock);
  const toolCalls: AgentToolCall[] = ordered
    .filter((block) => block.type === "tool_use")
    .map((block) => ({
      callId: String(block.start.id ?? ""),
      name: String(block.start.name ?? ""),
      arguments: block.partialJson.trim()
        ? block.partialJson
        : JSON.stringify(isRecord(block.start.input) ? block.start.input : {}),
    }));
  // A nameless call cannot be dispatched and an id-less one cannot be answered
  // with a paired `tool_result`, so the turn is unusable rather than degraded.
  // (Unparseable ARGUMENTS are different: `runCall` reports those back to the
  // model, which can then correct itself.)
  if (toolCalls.some((call) => !call.callId || !call.name)) {
    throw new AnthropicStreamError(
      "Anthropic returned a tool_use block without an id or a name",
      { code: "malformed_tool_use", transient: false },
    );
  }

  return { raw, toolCalls, text: textContent, reasoning: reasoningContent };
}

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Forwards thinking text to the caller as it streams, at most once.
 *
 * The sink downstream is append-only: the runner turns each partial into a
 * delta against the previous one and hands that to the trace narrator, which
 * cannot unsay anything. A replayed attempt re-streams the turn from its first
 * token, so its running text walks back over ground the discarded attempt
 * already narrated - which is precisely how a retry ends up showing the same
 * sentence twice.
 *
 * The accumulator itself resets per attempt, because each attempt gets its own
 * `readAnthropicMessagesStream` call. What survives across attempts is the
 * high-water mark of text actually handed to the caller, and only text that
 * continues it is forwarded: a replay of the same reasoning stays silent, and a
 * replay that thinks further narrates just the part that is new.
 *
 * Continuation rather than length is the test, because that is the arithmetic
 * the runner does: it slices off a tail only when a partial extends the last
 * one and otherwise narrates the partial whole. A replay whose thinking is
 * longer but worded differently satisfies a length gate while failing that
 * prefix check, so it would be read out in full beneath the abandoned
 * attempt's text. Such a replay is therefore left unnarrated - the high-water
 * mark is what the user has already read, and no continuation of it can be
 * spliced from thinking that never followed on from it. The turn's own
 * `reasoning` is untouched, so whichever attempt succeeds still contributes its
 * complete thinking to the transcript.
 */
function createReasoningRelay(
  onReasoning: ((partial: string) => void) | undefined,
): ((partial: string) => void) | undefined {
  if (!onReasoning) return undefined;
  let narrated = "";
  return (partial: string): void => {
    if (partial.length <= narrated.length || !partial.startsWith(narrated)) return;
    narrated = partial;
    onReasoning(partial);
  };
}

/**
 * Fetches and consumes one Messages iteration, replaying only transient
 * failures and only before any tool call is exposed to the caller.
 *
 * Reasoning streams out as it arrives rather than at the end of the turn: on a
 * long hop the thinking text is the only thing the task pane has to show, and
 * holding it until `message_stop` is the difference between a live trace and a
 * blank one. `requestOpenAIResponsesStreamWithRetry` still buffers its own
 * reasoning to the end of the attempt, so this is deliberately ahead of the
 * OpenAI path rather than in step with it. What the two keep in common is that
 * a discarded attempt cannot narrate twice - buffering is how that used to be
 * guaranteed here, and the relay is what guarantees it now.
 */
async function streamTurn(params: ExecutorStreamTurnParams): Promise<AgentTurnResult> {
  const maxBodyRetries = Math.max(0, params.maxBodyRetries ?? 1);
  const relayReasoning = createReasoningRelay(params.onReasoning);
  for (let attempt = 0; attempt <= maxBodyRetries; attempt += 1) {
    if (params.signal?.aborted) {
      const error = new Error("The operation was aborted");
      error.name = "AbortError";
      throw error;
    }
    try {
      const response = await params.request();
      if (!response.ok) {
        throw new AnthropicStreamError(
          await readModelError(response, params.contextLabel),
          {
            code: `http_${response.status}`,
            transient: TRANSIENT_HTTP_STATUSES.has(response.status),
            details: { retryAfterMs: parseRetryAfterMs(response) },
          },
        );
      }
      return await readAnthropicMessagesStream(
        response,
        params.onText,
        relayReasoning,
      );
    } catch (error) {
      if (
        params.signal?.aborted ||
        isAbortError(error) ||
        !isTransientAnthropicStreamError(error) ||
        attempt >= maxBodyRetries
      ) {
        throw error;
      }
      params.onRetry?.(error);
      const retryAfterMs =
        error instanceof AnthropicStreamError && isRecord(error.details)
          ? Number(error.details.retryAfterMs) || 0
          : 0;
      if (retryAfterMs > 0) await delay(retryAfterMs);
    }
  }
  throw new Error(`${params.contextLabel} stream retry exhausted`);
}

export const anthropicExecutorAdapter: ExecutorProviderAdapter = {
  provider: "anthropic",
  serializeTools: ({ scope, extraTools, options }: ExecutorSerializeToolsParams) =>
    buildToolDefinitionsForProvider("anthropic", scope, extraTools, options ?? {}) ?? [],
  encodeTurns,
  projectItems,
  buildRequest,
  streamTurn,
};
